import { useState, useEffect, useRef } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { MarkdownView } from '@/components/MarkdownView';
import { Button } from '@/components/ui/button';
import { Check } from 'lucide-react';

interface Props {
  value: string;
  onSave: (next: string) => Promise<void> | void;
  placeholder?: string;
  saveLabel?: string;
}

/**
 * Edit / preview tabbed markdown editor. Dirty state is local; explicit save
 * via the button (also Cmd/Ctrl+S). Mostly used for projects.notes_md.
 */
export function MarkdownEditor({ value, onSave, placeholder, saveLabel = 'Save' }: Props) {
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const lastSaved = useRef(value);

  useEffect(() => {
    setDraft(value);
    lastSaved.current = value;
  }, [value]);

  const dirty = draft !== lastSaved.current;

  async function handleSave() {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      await onSave(draft);
      lastSaved.current = draft;
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-3">
      <Tabs defaultValue="edit">
        <div className="flex items-center justify-between gap-3">
          <TabsList>
            <TabsTrigger value="edit">Edit</TabsTrigger>
            <TabsTrigger value="preview">Preview</TabsTrigger>
          </TabsList>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            {dirty ? <span>Unsaved</span> : <span className="inline-flex items-center gap-1"><Check className="h-3 w-3" /> Saved</span>}
            <Button size="sm" onClick={handleSave} disabled={!dirty || saving}>
              {saving ? 'Saving...' : saveLabel}
            </Button>
          </div>
        </div>
        <TabsContent value="edit">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={placeholder ?? 'Notes in markdown. Headings, lists, code blocks all supported.'}
            className="min-h-[320px] font-mono text-[13px] leading-relaxed"
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 's') {
                e.preventDefault();
                void handleSave();
              }
            }}
          />
        </TabsContent>
        <TabsContent value="preview">
          <div className="min-h-[320px] rounded-md border border-border bg-background/60 p-4">
            <MarkdownView source={draft} empty="Nothing to preview yet." />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
